export type CsChatSenderType = "member" | "client";

export type CsChatAttachment = {
  id: string;
  fileName: string;
  /**
   * 受信側では union にせず string として受ける。表示分岐は
   * `startsWith("image/")` で行うため、将来 sunsun 側が MIME を追加しても
   * karte のパースが壊れない。
   */
  contentType: string;
  sizeBytes: number;
};

export type CsChatMessage = {
  seq: number;
  id: string;
  senderType: CsChatSenderType;
  userId: string | null;
  clientSubject: string | null;
  clientDisplayName: string | null;
  body: string;
  attachments: CsChatAttachment[];
  createdAt: string;
};

export type CsChatChannel = {
  channelId: string;
  unreadCount: number;
  agent: string;
};

export type CsChatServerMessage =
  | {
      type: "hello";
      messages: CsChatMessage[];
    }
  | {
      type: "message";
      message: CsChatMessage;
    }
  | {
      type: "read";
      readerType: CsChatSenderType;
      readerKey: string;
      lastReadSeq: number;
    }
  | {
      type: "error";
      code: string;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isSenderType = (value: unknown): value is CsChatSenderType =>
  value === "member" || value === "client";

export const isCsChatAttachment = (
  value: unknown,
): value is CsChatAttachment => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    value.id.length > 0 &&
    typeof value.fileName === "string" &&
    value.fileName.length > 0 &&
    typeof value.contentType === "string" &&
    value.contentType.length > 0 &&
    typeof value.sizeBytes === "number" &&
    Number.isInteger(value.sizeBytes) &&
    value.sizeBytes > 0
  );
};

/**
 * attachments を正規化する。旧メッセージ・後方互換のため欠落は `[]` とみなす。
 * 存在する場合は全要素の構造チェックを要求し、不一致は `null`（メッセージごと
 * reject）を返す。
 */
const normalizeCsChatAttachments = (
  value: unknown,
): CsChatAttachment[] | null => {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value) || !value.every(isCsChatAttachment)) {
    return null;
  }

  return value;
};

export const parseCsChatMessage = (value: unknown): CsChatMessage | null => {
  if (!isRecord(value)) {
    return null;
  }

  const attachments = normalizeCsChatAttachments(value.attachments);
  if (
    attachments === null ||
    typeof value.seq !== "number" ||
    !Number.isInteger(value.seq) ||
    value.seq <= 0 ||
    typeof value.id !== "string" ||
    !isSenderType(value.senderType) ||
    !(typeof value.userId === "string" || value.userId === null) ||
    !(
      typeof value.clientSubject === "string" || value.clientSubject === null
    ) ||
    !(
      typeof value.clientDisplayName === "string" ||
      value.clientDisplayName === null
    ) ||
    typeof value.body !== "string" ||
    typeof value.createdAt !== "string"
  ) {
    return null;
  }

  return {
    seq: value.seq,
    id: value.id,
    senderType: value.senderType,
    userId: value.userId,
    clientSubject: value.clientSubject,
    clientDisplayName: value.clientDisplayName,
    body: value.body,
    attachments,
    createdAt: value.createdAt,
  };
};

export const parseCsChatMessageList = (
  value: unknown,
): CsChatMessage[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const messages: CsChatMessage[] = [];
  for (const item of value) {
    const parsed = parseCsChatMessage(item);
    if (!parsed) {
      return null;
    }
    messages.push(parsed);
  }

  return messages;
};

export const parseCsChatServerMessage = (
  value: unknown,
): CsChatServerMessage | null => {
  if (!isRecord(value) || typeof value.type !== "string") {
    return null;
  }

  if (value.type === "hello") {
    const messages = parseCsChatMessageList(value.messages);
    return messages ? { type: "hello", messages } : null;
  }

  if (value.type === "message") {
    const message = parseCsChatMessage(value.message);
    return message ? { type: "message", message } : null;
  }

  if (
    value.type === "read" &&
    isSenderType(value.readerType) &&
    typeof value.readerKey === "string" &&
    typeof value.lastReadSeq === "number" &&
    Number.isInteger(value.lastReadSeq)
  ) {
    return {
      type: "read",
      readerType: value.readerType,
      readerKey: value.readerKey,
      lastReadSeq: value.lastReadSeq,
    };
  }

  if (value.type === "error" && typeof value.code === "string") {
    return { type: "error", code: value.code };
  }

  return null;
};

export const parseCsChatChannel = (value: unknown): CsChatChannel | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (
    typeof value.channelId === "string" &&
    typeof value.unreadCount === "number" &&
    Number.isInteger(value.unreadCount) &&
    typeof value.agent === "string"
  ) {
    return {
      channelId: value.channelId,
      unreadCount: value.unreadCount,
      agent: value.agent,
    };
  }

  return null;
};

export const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
};
